<template>
  <Modal
    v-if="isVisible"
    :visible="isVisible"
    :title="capitalize($t('names.column', { n: 2 }))"
    size="md"
    @confirmed="confirmColumnChanges"
    @cancelled="close">
    <template #modal-after-form>
      <table class="table table-striped">
        <colgroup>
          <col />
          <col />
          <col width="100%" />
        </colgroup>
        <thead>
          <tr>
            <th />
            <th>{{ capitalize($t('states.visible')) }}</th>
            <th>{{ capitalize($t('names.column', { n: 1 })) }}</th>
          </tr>
        </thead>
        <tbody
          v-dropper="{
            list: columns,
            callback: (columns) => {
              onColumnOrderChanged(columns);
            },
          }"
          drag-class="dropper">
          <tr
            v-for="col in columns"
            :key="`col-visibility-${col.label}`"
            v-draggable="{ dragClass: 'dropper' }">
            <td>
              <div class="handle">
                <icon-menu />
              </div>
            </td>
            <td>
              <toggle-button
                :value="col.isVisible"
                :color="Colors.success"
                :speed="0"
                @change="col.isVisible = !col.isVisible" />
            </td>
            <td>{{ col.label }}</td>
          </tr>
        </tbody>
      </table>
      <p>
        <button
          class="btn btn-default btn-sm"
          @click="resetColumns">
          {{ $t('actions.resetColumns') }}
        </button>
      </p>
      <div
        v-if="hasNoColumnsSelected"
        class="alert alert-danger icon-xs flex-h">
        <icon-error />
        <span class="ml-1">{{ $t('messages.pleaseSelectAtLeastOneColumn') }}</span>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { capitalize, computed, ref, watch } from 'vue';

  import Colors from '@/Colors';
  import Modal from '@/common/components/controls/Modal.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import ToggleButton from '@/common/components/controls/ToggleButton.vue';
  import { IconError, IconMenu } from '@/common/icons/monochrome';

  interface IProps {
    editableColumns: TableColumn[];
  }

  const { editableColumns } = defineProps<IProps>();

  interface IEmits {
    (event: 'resetColumns'): void;
    (event: 'columnsUpdated', columns: TableColumn[]): void;
    (event: 'update:visible', value: boolean): void;
  }

  const emits = defineEmits<IEmits>();

  const isVisible = ref(false);
  const columns = ref([...editableColumns]);

  const hasNoColumnsSelected = computed(() => {
    return !columns.value.some((x) => x.isVisible);
  });

  function open() {
    isVisible.value = true;
    emits('update:visible', true);
  }

  function close() {
    isVisible.value = false;
    emits('update:visible', false);
  }

  function onColumnOrderChanged(list) {
    list.forEach((col, i) => {
      col.index = i;
    });
    columns.value = [...list];
  }

  function resetColumns() {
    emits('resetColumns');
  }

  function confirmColumnChanges() {
    if (hasNoColumnsSelected.value) {
      return;
    }

    emits('columnsUpdated', columns.value);
    close();
  }

  watch(
    () => editableColumns,
    (newVal) => {
      columns.value = [...newVal];
    },
  );

  defineExpose({
    open,
    close,
  });
</script>

<style scoped lang="scss">
  .handle {
    cursor: pointer;

    svg {
      fill: var(--textColor);
      stroke: var(--textColor);

      width: 1.5rem;
      height: 1.5rem;
      margin: 0 0.3rem -0.3rem 0.3rem;
    }
  }
</style>
